/**
 * Copilot subjects — which record a session is about.
 *
 * Pure helpers, so the rules that decide whether an entry point may exist at
 * all (a lead candidate has no application to open a session on) are tested
 * once rather than re-derived at each call site.
 */

/** Stable identity for a subject; "none" for a subject-less session. */
export const subjectKeyOf = (subject) =>
  subject?.kind && subject?.id !== null && subject?.id !== undefined
    ? `${subject.kind}:${subject.id}`
    : "none";

/**
 * The subject for the Results page, `/view-jobs-data/results/:activeTab/:id`.
 * `:id` is the JobApplication.id, except for a lead, which arrives with a
 * `leadCandidateId` query parameter (see useViewJobsNavigation).
 */
export function resultPageSubject(id, search = "") {
  if (id === null || id === undefined || id === "") return null;
  try {
    if (new URLSearchParams(search).has("leadCandidateId")) return null;
  } catch {
    return null;
  }
  return { kind: "application", id };
}

const SESSION_SUBJECT_KINDS = new Set(["job", "application", "requirement", "client"]);

/**
 * The subject a stored session was opened on, from a /copilot/sessions row.
 * Resuming has to reconnect with THIS subject, not the page's — otherwise a
 * session about one job is reopened with another job's id.
 */
export function subjectFromSession(session) {
  const kind = session?.subject_kind;
  const id = session?.subject_id;
  if (!SESSION_SUBJECT_KINDS.has(kind)) return null;
  if (id === null || id === undefined || id === "") return null;
  return { kind, id };
}

/**
 * The chat to continue when Hairo opens on `subject`: the newest row of a
 * /copilot/sessions list about this subject and active within `windowMs`.
 * Server order is not relied on. Returns the session id, or null.
 */
export function latestSessionFor(
  sessions,
  subject,
  { now = Date.now(), windowMs = Infinity } = {}
) {
  const key = subjectKeyOf(subject);
  if (key === "none" || !Array.isArray(sessions)) return null;
  let best = null;
  let bestAt = -Infinity;
  for (const session of sessions) {
    if (!session?.id) continue;
    // Keys compare as strings, so a page's 5 matches a row's "5".
    if (subjectKeyOf(subjectFromSession(session)) !== key) continue;
    const at = Date.parse(session.updated_at);
    if (!Number.isFinite(at) || now - at > windowMs) continue;
    if (at > bestAt) {
      best = session;
      bestAt = at;
    }
  }
  return best ? String(best.id) : null;
}
